import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useAuth } from "../../auth/AuthProvider";
import { Icon } from "../../components/icons";
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  Select,
  Textarea,
  buttonStyles,
  cx,
} from "../../components/ui";
import { ApiError } from "../../lib/api";
import { CATEGORIES, PRIORITIES } from "../../lib/support";
import { useApps, useCreateTicket, useDatabases, useUploadAttachment } from "../../lib/queries";
import type { SupportCategory, SupportPriority } from "../../lib/types";
import { AttachmentPicker } from "./AttachmentPicker";


/**
 * Opening a ticket.
 *
 * The form is deliberately short — subject, what it is about, the message — and
 * everything else is optional. Every required field is one more thing between
 * somebody with a problem and the sentence describing it, and a support form
 * that asks for an environment, a plan and a reproduction rate before it will
 * accept "my deploys stopped working" is a form people abandon in favour of
 * email.
 *
 * The rail beside it does the work a long form would: it says what to include,
 * because a first message with the app name and the timestamp in it saves an
 * entire round trip.
 */
export function NewTicketPage() {
  const { t } = useTranslation("support");
  const navigate = useNavigate();
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id ?? "";
  const [params] = useSearchParams();

  const create = useCreateTicket(orgId);
  const upload = useUploadAttachment(orgId);
  const apps = useApps(orgId || undefined, 100);
  const databases = useDatabases(orgId || undefined, 100);

  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState<SupportCategory>(
    (params.get("category") as SupportCategory | null) ?? "technical",
  );
  const [priority, setPriority] = useState<SupportPriority>("normal");
  /** One control for both kinds of workload: `app:<id>` or `database:<id>`. */
  const [workload, setWorkload] = useState(params.get("app") ? `app:${params.get("app")}` : "");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  const busy = create.isPending || upload.isPending;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !orgId) return;

    const [kind, id] = workload.split(":");

    const ticket = await create.mutateAsync({
      subject: subject.trim(),
      category,
      priority,
      message: message.trim(),
      appId: kind === "app" ? id : null,
      databaseId: kind === "database" ? id : null,
    });

    /*
     * Files go up after the ticket exists, because the API attaches them to a
     * message rather than to a form. A failed upload must not lose the ticket
     * that was already created — so it is reported on the thread the customer
     * is about to land on, not thrown back at this form.
     */
    let failed: string | null = null;
    for (const file of files) {
      try {
        await upload.mutateAsync({ ticketId: ticket.id, messageId: ticket.firstMessageId, file });
      } catch {
        failed = file.name;
      }
    }
    /* Reported on the thread rather than here: the ticket exists either way,
       and this page is about to be replaced by the one showing it. */
    navigate(`/support/${ticket.id}`, {
      state: failed ? { uploadFailed: failed } : undefined,
      replace: true,
    });
  };

  const error = create.error;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-[13px] text-fg-subtle">
        <Link to="/support" className="transition-colors hover:text-fg">
          {t("compose.crumb")}
        </Link>
        <Icon name="chevron" className="size-3" />
        <span className="text-fg-muted">{t("compose.crumbHere")}</span>
      </div>

      {/* A heading of its own rather than the breadcrumb doing double duty: the
          crumb is navigation, and a page with no `h1` is a page a screen reader
          announces as the layout it sits in. */}
      <header className="border-b border-line pb-5">
        <h1 className="text-[1.375rem] font-normal tracking-[-0.022em] text-fg">
          {t("compose.title")}
        </h1>
        <p className="mt-1 text-[13px] text-fg-muted">{t("compose.lead")}</p>
      </header>

      {/* Both columns `min-w-0` — see the note on the ticket page: a textarea
          contributes its `cols` width to a grid item's automatic minimum. */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0">
        <Card>
          <form onSubmit={submit} className="space-y-5 p-5">
            {error && (
              <Alert>
                {error instanceof ApiError
                  ? error.message
                  : t("compose.failed")}
              </Alert>
            )}

            <Field
              label={t("compose.subject")}
              htmlFor="subject"
              hint={t("compose.subjectHint")}
            >
              <Input
                id="subject"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                maxLength={200}
                required
                placeholder={t("compose.subjectPlaceholder")}
              />
            </Field>

            <div className="grid gap-5 sm:grid-cols-2">
              <Field
                label={t("compose.about")}
                htmlFor="category"
                hint={t(`category.${category}.hint`)}
              >
                <Select
                  id="category"
                  value={category}
                  onChange={(event) => setCategory(event.target.value as SupportCategory)}
                >
                  {CATEGORIES.map((value) => (
                    <option key={value} value={value}>
                      {t(`category.${value}.label`)}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field
                label={t("compose.impact")}
                htmlFor="priority"
                hint={t(`priority.${priority}.hint`)}
              >
                <Select
                  id="priority"
                  value={priority}
                  onChange={(event) => setPriority(event.target.value as SupportPriority)}
                >
                  {PRIORITIES.map((value) => (
                    <option key={value} value={value}>
                      {t(`priority.${value}.label`)}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            {/* Optional, and last of the selects: linking the workload saves the
                first round trip on most technical tickets, but nobody should
                have to find an app in a list before they can describe a
                problem that might not be about one. */}
            <Field
              label={t("compose.workload")}
              htmlFor="workload"
              hint={t("compose.workloadHint")}
            >
              <Select
                id="workload"
                value={workload}
                onChange={(event) => setWorkload(event.target.value)}
              >
                <option value="">{t("compose.workloadNone")}</option>
                {(apps.data?.data ?? []).length > 0 && (
                  <optgroup label={t("compose.apps")}>
                    {(apps.data?.data ?? []).map((app) => (
                      <option key={app.id} value={`app:${app.id}`}>
                        {app.name}
                      </option>
                    ))}
                  </optgroup>
                )}
                {(databases.data?.data ?? []).length > 0 && (
                  <optgroup label={t("compose.databases")}>
                    {(databases.data?.data ?? []).map((database) => (
                      <option key={database.id} value={`database:${database.id}`}>
                        {database.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </Select>
            </Field>

            <Field
              label={t("compose.message")}
              htmlFor="message"
              hint={t("compose.messageHint", { count: message.trim().length })}
            >
              <Textarea
                id="message"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                rows={10}
                maxLength={10_000}
                required
                placeholder={t("compose.messagePlaceholder")}
                className="font-mono text-[13px] leading-6"
              />
            </Field>

            <AttachmentPicker files={files} onChange={setFiles} disabled={busy} />

            <div className="flex flex-wrap items-center gap-3 border-t border-line pt-5">
              <Button type="submit" variant="primary" disabled={busy}>
                {busy ? t("compose.sending") : t("compose.send")}
              </Button>
              <Link to="/support" className={buttonStyles("ghost")}>
                {t("compose.cancel")}
              </Link>
              <span className="text-[13px] text-fg-subtle">
                {t("compose.visibility", {
                  organization: currentOrg?.name ?? t("compose.thisOrganization"),
                })}
              </span>
            </div>
          </form>
        </Card>
        </div>

        <aside className="min-w-0 space-y-4">
          <GuidanceCard
            icon="logs"
            title={t("compose.helps.title")}
            points={t("compose.helps.points", { returnObjects: true })}
          />

          {category === "abuse" ? (
            <GuidanceCard
              icon="shield"
              title={t("compose.vulnerability.title")}
              points={t("compose.vulnerability.points", { returnObjects: true })}
            />
          ) : (
            <GuidanceCard
              icon="announcements"
              title={t("compose.before.title")}
              points={t("compose.before.points", { returnObjects: true })}
            />
          )}
        </aside>
      </div>
    </div>
  );
}

function GuidanceCard({
  icon,
  title,
  points,
}: {
  icon: "logs" | "shield" | "announcements";
  title: string;
  points: string[];
}) {
  return (
    <div className={cx("card p-5")}>
      <div className="flex items-center gap-2">
        <Icon name={icon} className="size-3.5 text-fg-subtle" />
        <h2 className="text-[13px] font-medium text-fg">{title}</h2>
      </div>
      <ul className="mt-3 space-y-2">
        {points.map((point) => (
          <li key={point} className="flex gap-2 text-[13px] leading-6 text-fg-muted">
            <span aria-hidden="true" className="mt-2.5 size-1 shrink-0 rounded-full bg-line-strong" />
            <span>{point}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
